import * as React from "react";
import { useEffect, useState } from "react";
import styled from "@mui/material/styles/styled";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import { useHistory, useRouteMatch } from "react-router";
import { ClinicThemeCustomization } from "fe-web-components/settings/themeCustomization/clinicThemeCustomization";
import { Button, Dialog, DialogActions, IconButton } from "@mui/material";
import {
  ConfigurationTab,
  KahunSliderTabs,
} from "fe-web-components/settings/presentaional/kahunSliderTabs";
import { JsonSettingsEdit } from "fe-web-components/settings/presentaional/jsonSettingsEdit";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { ClinicActionButton } from "fe-web-components/clinic/clinicActionButton";
import { migrateClinicSettingsToV2 } from "fe-web-components/settings/common/migrateClinicSettingsToV2";
import DialogContent from "@mui/material/DialogContent";
import { useUpdateClinicConfig } from "./hooks/useUpdateClinicConfig";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { useClinicState } from "../../clinic/clinicStateContext";

//region [[ Styles ]]
const ClinicSettingsView = styled("div")(({ theme }) => ({}));

const MigrationMessage = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
  height: "0",
  overflow: "hidden",
  width: "100%",
  background: "#efefef",
  fontWeight: 600,
  color: "#232323",
  transition: "all 0.8s ease-out",
  margin: "15px 0",
  "&.show": {
    height: "60px",
    border: "1px dashed #5a52c3",
  },
});

const CloseMigrationMessageButton = styled(IconButton)({
  position: "absolute",
  right: "15px",
  top: "5px",
});

const MigrationButton = styled(ClinicActionButton)({
  height: "fit-content",
  padding: "4px 15px",
  margin: "15px",
  width: "fit-content",
});
//endregion [[ Styles ]]

//region [[ Props ]]
export interface ClinicSettingsProps {}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]
export const ClinicV1Settings = ({ ...props }: ClinicSettingsProps) => {
  const match = useRouteMatch<{ key: string }>();

  const history = useHistory();

  const [
    {
      clinicData: { id: clinicId },
    },
  ] = useClinicState();

  const {
    config,
    getDefaultValueFor,
    createStoreKeyHandler,
    createResetDefaultHandler,
    isReady,
  } = useUpdateClinicConfig();

  useEffect(() => {
    if (isReady) setTimeout(() => setShowMigrationMessage(true), 1000);
  }, [isReady]);

  const getIndexBySelectedKey = () => {
    let selectedIndex = 0;
    Object.entries(ClinicConfigKeys).forEach(([name, key], index) => {
      if (match?.params?.key === key) selectedIndex = index;
    });
    return selectedIndex;
  };

  const [showMigrationMessage, setShowMigrationMessage] = useState(false);

  const [showMigrationPopup, setShowMigrationPopup] = useState(null);

  if (match?.params?.key === ClinicConfigKeys.Theme) {
    return (
      <ClinicSettingsView>
        <ClinicThemeCustomization
          clinicId={clinicId}
          defaultTheme={getDefaultValueFor(ClinicConfigKeys.Theme)}
          onSave={createStoreKeyHandler(ClinicConfigKeys.Theme)}
        />
      </ClinicSettingsView>
    );
  }

  const configurationTabs: ConfigurationTab[] = Object.entries(ClinicConfigKeys)
    .filter(([name, key]) => key !== ClinicConfigKeys.UnifiedConfigString)
    .map(([name, key], index) => ({
      key,
      name,
      tooltip: null,
      component: (
        <JsonSettingsEdit
          keyName={name}
          key={key}
          value={config[key] || {}}
          onSubmit={createStoreKeyHandler(key)}
          onResetDefaultClicked={createResetDefaultHandler(key)}
        />
      ),
    }));

  const hideMigrationMessage = () => setShowMigrationMessage(null);

  const migrateClinic = () => {
    const migratedNewSettings = migrateClinicSettingsToV2(config);
    setShowMigrationPopup(migratedNewSettings);
  };

  const saveMigratedObject = () => {
    createStoreKeyHandler(ClinicConfigKeys.UnifiedConfigString)({
      ...showMigrationPopup,
    });
    setShowMigrationPopup(null);
  };

  if (!isReady) return <ThreeDotsLoader />;
  return (
    <ClinicSettingsView className={"clinic-settings-view"}>
      <Dialog
        open={!!showMigrationPopup}
        onClose={() => setShowMigrationPopup(null)}
      >
        <DialogContent>
          Following data was created:
          <pre>
            <code>{JSON.stringify(showMigrationPopup, null, 2)}</code>
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={saveMigratedObject}>approve</Button>
          <Button onClick={() => setShowMigrationPopup(null)}>cancel</Button>
        </DialogActions>
      </Dialog>
      <MigrationMessage className={showMigrationMessage ? "show" : "hide"}>
        <CloseMigrationMessageButton onClick={hideMigrationMessage}>
          <HighlightOffIcon />
        </CloseMigrationMessageButton>
        This clinic uses older settings structure
        <MigrationButton
          onClick={migrateClinic}
          label={"Upgrade to new settings"}
        />
      </MigrationMessage>
      <Button onClick={() => history.push(match.url + "/theme")}>
        Theme customization
      </Button>
      <KahunSliderTabs
        tabs={configurationTabs}
        selectedIndex={getIndexBySelectedKey()}
      />
    </ClinicSettingsView>
  );
};
